/**
 * Batch Processes Detail Handlers
 *
 * Pure command handlers for process data, reference data,
 * and loading state management.
 */

import { BatchProcessesDetailState, initialState } from './state';
import { BatchProcessesDetailCommand } from './commands';
import { CommandHandlerMap } from '@commandDrivenStateEngine/core/types';

type ExtractCommand<T extends string> = Extract<BatchProcessesDetailCommand, { type: T }>;

// -------- Data handlers ----------

const handleSetProcess = (
  state: BatchProcessesDetailState,
  cmd: ExtractCommand<'batchProcesses/detail/data/setProcess'>
): BatchProcessesDetailState => ({
  ...state,
  process: cmd.payload.process,
  loading: { ...state.loading, process: false }
});

const handleUpdateProcess = (
  state: BatchProcessesDetailState,
  cmd: ExtractCommand<'batchProcesses/detail/data/updateProcess'>
): BatchProcessesDetailState => ({
  ...state,
  process: state.process
    ? { ...state.process, ...cmd.payload.process }
    : cmd.payload.process
});

const handleClearProcess = (
  _state: BatchProcessesDetailState,
  _cmd: ExtractCommand<'batchProcesses/detail/data/clearProcess'>
): BatchProcessesDetailState => initialState;

const handleSetResolvedPublications = (
  state: BatchProcessesDetailState,
  cmd: ExtractCommand<'batchProcesses/detail/data/setResolvedPublications'>
): BatchProcessesDetailState => ({
  ...state,
  resolvedPublications: cmd.payload.publications
});

const handleSetResolvedWarehouses = (
  state: BatchProcessesDetailState,
  cmd: ExtractCommand<'batchProcesses/detail/data/setResolvedWarehouses'>
): BatchProcessesDetailState => ({
  ...state,
  resolvedWarehouses: cmd.payload.warehouses
});

const handleSetResolvedChannels = (
  state: BatchProcessesDetailState,
  cmd: ExtractCommand<'batchProcesses/detail/data/setResolvedChannels'>
): BatchProcessesDetailState => ({
  ...state,
  resolvedChannels: cmd.payload.channels
});

const handleSetRevokedChannelTargets = (
  state: BatchProcessesDetailState,
  cmd: ExtractCommand<'batchProcesses/detail/data/setRevokedChannelTargets'>
): BatchProcessesDetailState => ({
  ...state,
  revokedChannelTargets: cmd.payload.integrationIds
});

// -------- Loading handlers ----------

const handleSetProcessLoading = (
  state: BatchProcessesDetailState,
  cmd: ExtractCommand<'batchProcesses/detail/loading/setProcessLoading'>
): BatchProcessesDetailState => ({
  ...state,
  loading: { ...state.loading, process: cmd.payload.isLoading }
});

const handleSetReferenceDataLoading = (
  state: BatchProcessesDetailState,
  cmd: ExtractCommand<'batchProcesses/detail/loading/setReferenceDataLoading'>
): BatchProcessesDetailState => ({
  ...state,
  loading: { ...state.loading, referenceData: cmd.payload.isLoading }
});

const handleSetProjectedSuccessCount = (
  state: BatchProcessesDetailState,
  cmd: ExtractCommand<'batchProcesses/detail/data/setProjectedSuccessCount'>
): BatchProcessesDetailState => ({
  ...state,
  projectedSuccessCount: cmd.payload.count,
  loading: { ...state.loading, projectedSuccessCount: false }
});

const handleSetProjectedSuccessCountLoading = (
  state: BatchProcessesDetailState,
  cmd: ExtractCommand<'batchProcesses/detail/loading/setProjectedSuccessCountLoading'>
): BatchProcessesDetailState => ({
  ...state,
  loading: { ...state.loading, projectedSuccessCount: cmd.payload.isLoading }
});

// -------- Handler map ----------

export const handlers: CommandHandlerMap<BatchProcessesDetailState, BatchProcessesDetailCommand> = {
  // Data
  'batchProcesses/detail/data/setProcess': handleSetProcess,
  'batchProcesses/detail/data/updateProcess': handleUpdateProcess,
  'batchProcesses/detail/data/clearProcess': handleClearProcess,
  'batchProcesses/detail/data/setResolvedPublications': handleSetResolvedPublications,
  'batchProcesses/detail/data/setResolvedWarehouses': handleSetResolvedWarehouses,
  'batchProcesses/detail/data/setResolvedChannels': handleSetResolvedChannels,
  'batchProcesses/detail/data/setProjectedSuccessCount': handleSetProjectedSuccessCount,
  'batchProcesses/detail/data/setRevokedChannelTargets': handleSetRevokedChannelTargets,
  // Loading
  'batchProcesses/detail/loading/setProcessLoading': handleSetProcessLoading,
  'batchProcesses/detail/loading/setReferenceDataLoading': handleSetReferenceDataLoading,
  'batchProcesses/detail/loading/setProjectedSuccessCountLoading': handleSetProjectedSuccessCountLoading
};
